import "../styles/globals.css";
import { store } from "@/Redux/Store";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "@/theme";
import Head from "next/head";
import CommonActionsWraper from "@/Components/Common/CommonActionsWraper";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { NoSsr } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { publicPaths } from "@/lib/utils";
import { ConfirmProvider } from "material-ui-confirm";
import { AppProps } from "next/app";
import MainLayout from "@/Components/Layout/MainLayout";
import "@/lib/dayjs";

import { getAuthToken } from "@/lib/authCookies";

const muiCache = createCache({
  key: "mui",
  prepend: true,
});

export default function MyApp({ Component, pageProps }: AppProps) {
  // ============= Hooks =============
  const router = useRouter();
  // ============= Effects =============
  useEffect(() => {
    if (!publicPaths.some((path) => router.asPath.startsWith(path))) {
      const token = getAuthToken();
      if (!token) {
        router.replace("/login");
      }
    }
  }, [router?.asPath]);
  return (
    <>
      <Head>
        <title>{pageProps?.PageTitle || "The Food Exchange"}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content={pageProps?.MetaDescription || "The Food Exchange"} />
      </Head>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <ConfirmProvider
            defaultOptions={{
              allowClose: false,
              title: "",
              cancellationText: "Cancel",
              confirmationText: "Delete",
              dialogProps: {
                PaperProps: {
                  sx: {
                    width: "600px",
                    borderRadius: "16px",
                    background: theme.palette.background.default,
                    p: 1.5,
                    userSelect: "none",
                  },
                },
              },
              confirmationButtonProps: {
                variant: "contained",
                sx: {
                  background: theme.palette.primary.main,
                  color: theme.palette.custom.white,
                  borderRadius: "8px",
                  minHeight: "46px",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                  px: 4,
                  "&:hover": {
                    background: theme.palette.primary.main,
                    boxShadow: "none",
                  },
                },
              },
              cancellationButtonProps: {
                variant: "outlined",
                sx: {
                  background: theme.palette.custom.white,
                  color: theme.palette.primary.main,
                  borderRadius: "8px",
                  minHeight: "46px",
                  border: `1px solid ${theme.palette.primary.main}`,
                  fontWeight: 600,
                  textTransform: "none",
                  px: 4,
                  mr: 1,
                  "&:hover": {
                    background: theme.palette.custom.white,
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                },
              },
              titleProps: {
                sx: {
                  color: theme.palette.primary.main,
                  fontSize: "22px",
                  fontWeight: 700,
                  p: 1,
                },
              },
              contentProps: {
                sx: {
                  p: 1,
                  mb: 2,
                  "& .MuiDialogContentText-root": {
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                  },
                },
              },
            }}
          >
            <CssBaseline />
            <CacheProvider value={muiCache}>
              <NoSsr>
                <CommonActionsWraper />
                {publicPaths.some((path) => router.asPath.startsWith(path)) ? (
                  <Component {...pageProps} />
                ) : (
                  <MainLayout>
                    <Component {...pageProps} />
                  </MainLayout>
                )}
              </NoSsr>
            </CacheProvider>
          </ConfirmProvider>
        </ThemeProvider>
      </Provider>
    </>
  );
}
